import React, { useEffect, useRef, useState } from "react";
import { RCCAlert, RCCBadge, RCCButton, RCCGrid, RCCHookToast, RCCIcon, RCCServicesApi } from "react-common-components";
import { useHistory } from "react-router-dom";
import Loader from "src/components/Loader";
import PageTitle from "src/components/PageTitle";
import Title from "src/components/Title";
import { useAuth } from "src/context/Auth/context";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { IPresentationContentComponent } from "../../interface";
import { IOftbp001 } from "../Modals/interface";
import StyledButton, { StyledAppIcons } from "./styles";
import ModalImportStructure from "../ModalImportStructure";
import { IRefModalImportStructure } from "../ModalImportStructure/interface";

const PresentationGroupContentComponent: React.FC<IPresentationContentComponent> = ({
	selectedBudgetPlan
}) => {
	const {translate} = useTranslation();
	const {confirm} = useConfirmModal();
	const history = useHistory();
	const { administrator, superUser } = useAuth();
	const { addToast } = RCCHookToast();
	const [corporativeValuesUpdated, setCorporativeValueUpdated] = useState(selectedBudgetPlan?.corporativeValuesUpdated !== false);

	const modalImportStructureRef = useRef<IRefModalImportStructure>(null);

	const [errorLoading, setErrorLoading] = useState(false);
	const [loading, setLoading] = useState(true);
	const [permissions, setPermissions] = useState<IOftbp001[]>([]);

	const updateCorporativeValues = () => {
		confirm(
			translate("Atualizar valores corporativos"), 
			`${translate("Esse processo pode demorar alguns minutos, deseja continuar")}?`,
			async () => {
				if(!selectedBudgetPlan)
					return;

				await RCCServicesApi.putRequest({
					method: "FinancialManagement/UpdateCorporativeValues",
					urlParameters: {
						budgetPlanId: selectedBudgetPlan.id
					}
				});

				setCorporativeValueUpdated(true);

				addToast({
					color: "green",
					description: translate("Valores corporativos atualizados com sucesso"),
					title: translate("Sucesso")
				});
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao atualizar valores corporativos"), addToast);
			},
			false
		);
	};

	const loadData = async () => {
		if(!selectedBudgetPlan)
			return;
			
		try{
			setLoading(true);
			setErrorLoading(false);

			const {data} = await RCCServicesApi.getRequest({
				method: "FinancialManagement/SelectBudgetPlanPermissions",
				urlParameters: {
					budgetPlanId: selectedBudgetPlan.id
				}
			});
			setPermissions(data);
			setLoading(false);
		}catch(err){
			standardErrorMessages(err, translate("Erro ao carregar os dados"), addToast);
			setLoading(false);
			setErrorLoading(true);
		}
	};

	useEffect(() => {
		loadData();
		setCorporativeValueUpdated(selectedBudgetPlan?.corporativeValuesUpdated !== false);
	}, [selectedBudgetPlan]);

	if(selectedBudgetPlan === undefined)
		return <RCCAlert 
			title={translate("Atenção")}
			description={translate("Selecione um plano orçamentário ao lado")}
		/>;

	if(loading)
		return <Loader />;


	if(errorLoading)
		return <RCCAlert 
			title={translate("Atenção")}
			description={translate("Não foram encontrados dados")}
		/>;

	return (
		<>
			<RCCGrid container item direction="column">

				{/* Header -> Results + Buttons */}
				<PageTitle
					title={selectedBudgetPlan?.title || ""}
				>
					<div style={{ 
						display: "flex",
						alignItems: "center" 
					}}>
						{selectedBudgetPlan.draft && (
							<RCCBadge color="grey">
								<RCCIcon name="pencil" color="#FFF" /> &nbsp; {translate("Rascunho")}
							</RCCBadge>
						)}
						{!corporativeValuesUpdated && (
							<RCCButton color="yellow" onClick={updateCorporativeValues}>
								<RCCIcon name="exclamationTriangle" /> &nbsp; {translate("Valores corporativos desatualizados")}
							</RCCButton>
						)}
					</div>
				</PageTitle>
				{(administrator || superUser || permissions.find((x) => x.ofp001Permissao === 1)) && (
					<>
						<Title color="purple" size="md">
							{translate("Unidade/Filial")}
						</Title>
						<RCCGrid container item>
							<StyledButton 
								onClick={() => history.push(`/financialmanagement/subsidiary/${selectedBudgetPlan.id}?name=${selectedBudgetPlan.title}`)}>
								<RCCIcon 
									name="building"
									color="var(--purple-lighter)"
									size={16}
								/>
								<Title color="purple" size="sm">{translate("Unidade/Filial")}</Title>
							</StyledButton>
						</RCCGrid>
					</>
				)}
				{(administrator || superUser || permissions.find((x) => x.ofp001Permissao === 1 || x.ofp001Permissao === 2  || x.ofp001Permissao === 4 )) && (
					<>
						<Title color="purple" size="md">
							{translate("Orçamentos")}
						</Title>
						<RCCGrid container item>
							{(administrator || superUser || permissions.find((x) => x.ofp001Permissao === 1 || (x.ofp001Permissao === 2 && x.ofp001Acessore === "S") || (x.ofp001Permissao === 4 && x.ofp001Acessore === "S") )) && (
								<StyledButton 
									onClick={() => history.push(`/financialmanagement/salesrevenue/${selectedBudgetPlan.id}?name=${selectedBudgetPlan.title}`)}>
									<StyledAppIcons
										name="financialManagement"
										size={24}
										fill="var(--green-light)"
									/>
									<Title color="purple" size="sm">{translate("Receita de vendas")}</Title>
								</StyledButton>
							)}
							{(administrator || superUser || permissions.find((x) => x.ofp001Permissao === 1 || (x.ofp001Permissao === 2 && x.ofp001Acessode === "S") || (x.ofp001Permissao === 4 && x.ofp001Acessode === "S") )) && (
								<StyledButton 
									onClick={() => history.push(`/financialmanagement/salesdeduction/${selectedBudgetPlan.id}?name=${selectedBudgetPlan.title}`)}>
									<StyledAppIcons
										name="financialManagement"
										size={24}									
										fill="var(--red-light)"
									/>
									<Title color="purple" size="sm">{translate("Deduções de vendas")}</Title>
								</StyledButton>
							)}
							{(administrator || superUser || permissions.find((x) => x.ofp001Permissao === 1 || (x.ofp001Permissao === 2 && x.ofp001Acessocv === "S") || (x.ofp001Permissao === 4 && x.ofp001Acessocv === "S") )) && (
								<StyledButton 
									onClick={() => history.push(`/financialmanagement/variablecosts/${selectedBudgetPlan.id}?name=${selectedBudgetPlan.title}`)}>
									<StyledAppIcons
										name="financialManagement"
										size={24}									
										fill="var(--red-light)"
									/>
									<Title color="purple" size="sm">{translate("Custos variáveis")}</Title>
								</StyledButton>
							)}
							{(administrator || superUser || permissions.find((x) => x.ofp001Permissao === 1 || (x.ofp001Permissao === 2 && x.ofp001Acessodv === "S") || (x.ofp001Permissao === 4 && x.ofp001Acessodv === "S") )) && (
								<StyledButton
									onClick={() => history.push(`/financialmanagement/variableexpenses/${selectedBudgetPlan.id}?name=${selectedBudgetPlan.title}`)}>
									<StyledAppIcons
										name="financialManagement"
										size={24}									
										fill="var(--red-light)"
									/>
									<Title color="purple" size="sm">{translate("Despesas variáveis")}</Title>
								</StyledButton>
							)}
							{(administrator || superUser || permissions.find((x) => x.ofp001Permissao === 1 || (x.ofp001Permissao === 2 && x.ofp001Acessodo === "S") || (x.ofp001Permissao === 4 && x.ofp001Acessodo === "S") )) && (
								<StyledButton 
									onClick={() => history.push(`/financialmanagement/operationalexpenses/${selectedBudgetPlan.id}?name=${selectedBudgetPlan.title}`)}>
									<StyledAppIcons
										name="financialManagement"
										size={24}									
										fill="var(--red-light)"
									/>
									<Title color="purple" size="sm">{translate("Despesas operacionais")}</Title>
								</StyledButton>
							)}
							{(administrator || superUser || permissions.find((x) => x.ofp001Permissao === 1 || (x.ofp001Permissao === 2 && x.ofp001Acessoou === "S") || (x.ofp001Permissao === 4 && x.ofp001Acessoou === "S") )) && (
								<StyledButton
									onClick={() => history.push(`/financialmanagement/otherexpenses/${selectedBudgetPlan.id}?name=${selectedBudgetPlan.title}`)}>
									<StyledAppIcons
										name="financialManagement"
										size={24}
										fill="var(--red-light)"
									/>
									<Title color="purple" size="sm">{translate("Outras despesas")}</Title>
								</StyledButton>
							)}
						</RCCGrid>
					</>
				)}
				{(administrator || superUser || permissions.find((x) => x.ofp001Permissao === 1 || x.ofp001Permissao === 3  || x.ofp001Permissao === 4)) && (
					<>
						<Title color="purple" size="md">
							{translate("Demonstrativos")}
						</Title>
						<RCCGrid container item>
							<StyledButton
								onClick={() => history.push(`/financialmanagement/incomestatement/${selectedBudgetPlan.id}?name=${selectedBudgetPlan.title}`)}>
								<RCCIcon 
									name="list"
									color="var(--purple-lighter)"
									size={16}
								/>
								<Title color="purple" size="sm">{translate("Demonstrativo resultado do exercício")}</Title>
							</StyledButton>
							<StyledButton
								onClick={() => history.push(`/financialmanagement/performance/${selectedBudgetPlan.id}?name=${selectedBudgetPlan.title}`)}>
								<RCCIcon 
									name="list"
									color="var(--purple-lighter)"
									size={16}
								/>
								<Title color="purple" size="sm">Performance</Title>
							</StyledButton>
						</RCCGrid>
					</>
				)}
				{(administrator || superUser || permissions.find((x) => x.ofp001Permissao === 1 || x.ofp001Permissao === 2 || x.ofp001Permissao === 3 || x.ofp001Permissao === 4)) && (
					<>
						<Title color="purple" size="md">
							{translate("Consulta")}
						</Title>
						<RCCGrid container item>
							<StyledButton
								onClick={() => {
									const fullYearStart = new Date(selectedBudgetPlan.startDate).getFullYear();
									const fullYearEnd = new Date(selectedBudgetPlan.endDate).getFullYear();
									history.push(`/financialmanagement/consultvalues/${selectedBudgetPlan.id}?name=${selectedBudgetPlan.title}&startYear=${fullYearStart.toString()}&endYear=${fullYearEnd.toString()}`);
								}}>
								<RCCIcon 
									name="table"
									color="var(--purple-lighter)"
									size={16}
								/>
								<Title color="purple" size="sm">{translate("Consulta valores de orçamento")}</Title>
							</StyledButton>
							{(administrator || superUser || permissions.find((x) => x.ofp001Permissao === 1)) && (
								<StyledButton
									onClick={() => history.push(`/financialmanagement/budgetlogs/${selectedBudgetPlan.id}?name=${selectedBudgetPlan.title}`)}>
									<RCCIcon 
										name="table"
										color="var(--purple-lighter)"
										size={16}
									/>
									<Title color="purple" size="sm">{translate("Logs de orçamento")}</Title>
								</StyledButton>
							)}
						</RCCGrid>
					</>
				)}
				<Title color="purple" size="md">
					{translate("Indicadores")}
				</Title>
				<RCCGrid>
					<StyledButton
						onClick={() => history.push(`/indicators/panel?group=9&budgetPlan=${selectedBudgetPlan.id}`)}>
						<RCCIcon 
							name="chartBar"
							color="var(--purple-lighter)"
							size={16}
						/>
						<Title color="purple" size="sm">{translate("Indicadores")}</Title>
					</StyledButton>
				</RCCGrid>		
				{(administrator || superUser || permissions.find((x) => x.ofp001Permissao === 1)) && (
					<>
						<Title color="purple" size="md">
							{translate("Integração")}
						</Title>
						<RCCGrid>
							<StyledButton
								onClick={() => {
									if(!modalImportStructureRef.current)
										return;

									modalImportStructureRef.current.openModalImportStructure(selectedBudgetPlan.id, selectedBudgetPlan.title);
								}}>
								<RCCIcon 
									name="upload"
									color="var(--purple-lighter)"
									size={16}
								/>
								<Title color="purple" size="sm">{translate("Integrar valores realizados")}</Title>
							</StyledButton>
						</RCCGrid>
					</>
				)}		
			</RCCGrid>
			<ModalImportStructure 
				ref={modalImportStructureRef}
			/>
		</>
	);
};

export default PresentationGroupContentComponent;
